'use client';

import type { ProgressTextPlace } from '../cards/registry';

function clampPct(n: number): number {
  return Math.max(0, Math.min(100, n));
}

export interface CourseProgressBarProps {
  /** 0–100. Caller decides whether to render at all (the {@code show}
   *  admin toggle and course-has-progress checks happen before this). */
  progress: number;
  /** Adds the rounded percentage label. Defaults to hidden. */
  showText?: boolean;
  /** {@code 'outside-*'} places the label beside the track; {@code
   *  'inside-*'} overlays it on the track itself (centered, or pinned to
   *  an edge) — inside placements grow the track tall enough to hold the
   *  label. Defaults to {@code 'outside-right'}. */
  textPlace?: ProgressTextPlace;
  /** Track height in the default ('outside') placements. Inside
   *  placements grow to fit the label regardless of this value. */
  height?: number;
  /** Track/fill corner radius. Defaults to a full pill (half the track
   *  height) — pass {@code 0} for square corners, or any CSS
   *  {@code border-radius} value. */
  radius?: number | string;
  trackColor?: string;
  fillColor?: string;
  /** Color of the outside label, and of the inside track's base (unfilled)
   *  copy of the label. */
  labelColor?: string;
  /** Color of the inside label's copy clipped to the filled width —
   *  needs to read against {@code fillColor}, not {@code trackColor}. */
  insideFillLabelColor?: string;
}

/**
 * Course-progress track shared by every card variant so {@code showText}/
 * {@code textPlace} behave identically everywhere a progress bar appears.
 *
 * <p>Inside labels overlay both the filled and unfilled parts of the
 * track, which can land on either a light (unfilled) or brand-colored
 * (filled) background — a single text color reads well on one and
 * disappears on the other (e.g. white-on-white at 0%). Two stacked
 * copies fix that: a base label (readable on the track) plus a copy
 * clipped to exactly the filled width (readable on the fill).
 */
export function CourseProgressBar({
  progress,
  showText = false,
  textPlace = 'outside-right',
  height = 4,
  radius,
  trackColor = 'rgba(0,0,0,0.15)',
  fillColor = 'var(--color-progress, #3b5bdb)',
  labelColor = 'var(--color-text-secondary, #4a4a4a)',
  insideFillLabelColor = '#fff',
}: CourseProgressBarProps) {
  const pct = clampPct(progress);
  const isInside = textPlace.startsWith('inside');
  const trackHeight = isInside ? Math.max(height, 16) : height;

  const insideLabelBaseStyle = {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      textPlace === 'inside-left'
        ? 'flex-start'
        : textPlace === 'inside-right'
          ? 'flex-end'
          : 'center',
    padding: textPlace === 'inside-center' ? 0 : '0 6px',
    fontSize: 10,
    fontWeight: 700,
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexDirection: textPlace === 'outside-left' ? 'row-reverse' : 'row',
      }}
    >
      <div
        style={{
          flex: 1,
          position: 'relative',
          height: trackHeight,
          borderRadius: radius ?? (isInside ? trackHeight / 2 : height / 2),
          background: trackColor,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: fillColor }} />
        {isInside && showText && (
          <>
            <span aria-hidden style={{ ...insideLabelBaseStyle, color: labelColor }}>
              {Math.round(pct)}%
            </span>
            <span
              style={{
                ...insideLabelBaseStyle,
                color: insideFillLabelColor,
                clipPath: `inset(0 ${100 - pct}% 0 0)`,
              }}
            >
              {Math.round(pct)}%
            </span>
          </>
        )}
      </div>
      {showText && !isInside && (
        <span style={{ fontSize: 12, fontWeight: 700, color: labelColor, flexShrink: 0 }}>
          {Math.round(pct)}%
        </span>
      )}
    </div>
  );
}
